/**
 * Placements.jsx
 * Which platforms the advert runs on.
 *
 * Worth being explicit about, because Meta's default is not "Facebook and
 * Instagram" — it is automatic placements, which also spends on Messenger and
 * Audience Network. Those are places this app can neither preview nor read
 * results back from, so they are not offered and the ad says so.
 *
 * The control has two modes. For an advert written here it asks, because one
 * creative renders natively on both. For an advert built from live posts it
 * only reports: a post can run where it was posted and nowhere else, so the
 * posts chosen decide this and letting it be clicked would be a lie.
 *
 * Dependencies: components/Icons.jsx
 */

import { Check, Warning } from '../Icons.jsx';

const NAME = { facebook: 'Facebook', instagram: 'Instagram' };

export default function Placements({ options, value, connected, derived, onChange }) {
  return (
    <div className="field">
      <label>Where it runs</label>

      <div className="btn-row">
        {options.map((p) => {
          const isConnected = connected.includes(p.value);
          const on = value.includes(p.value);
          const disabled = derived || !isConnected;

          return (
            <button
              key={p.value}
              type="button"
              className={`btn btn--sm ${on ? 'btn--primary' : ''}`}
              disabled={disabled}
              title={
                !isConnected
                  ? `${p.label} is not connected for this brand.`
                  : derived
                    ? 'Decided by the posts you chose above.'
                    : undefined
              }
              onClick={() => onChange(on ? value.filter((x) => x !== p.value) : [...value, p.value])}
            >
              {on ? <Check size={15} /> : null}
              {p.label}
              {!isConnected ? ' — not connected' : ''}
            </button>
          );
        })}
      </div>

      <Explain derived={derived} value={value} />
    </div>
  );
}

function Explain({ derived, value }) {
  if (derived) {
    if (value.length === 0) {
      return <p className="hint">Choose a post above and this fills itself in.</p>;
    }
    if (value.length === 2) {
      return (
        <p className="hint">
          Both, from the two posts you chose — one ad each, inside a single ad set. That is what
          puts them on one budget rather than two.
        </p>
      );
    }
    return (
      <p className="hint">
        {NAME[value[0]]} only, because that is where the post you chose lives. Choose a{' '}
        {value[0] === 'facebook' ? 'Instagram' : 'Facebook'} post as well to cover both.
      </p>
    );
  }

  if (value.length === 0) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>An advert has to run somewhere. Choose Facebook, Instagram, or both.</span>
      </div>
    );
  }

  return (
    <p className="hint">
      {value.length === 2
        ? 'One advert, shown on both — Meta splits the budget towards whichever performs.'
        : `Only ${NAME[value[0]]}. The whole budget goes there.`}{' '}
      Messenger and Audience Network are deliberately never used.
    </p>
  );
}
